import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Zap, Shield, TrendingUp } from 'lucide-react';
import { aboutSections, howItWorks } from '../mock/data';
import { Button } from '../components/ui/button';
import './About.css';

const About = () => {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero-section">
        <div className="section-content">
          <h1 className="display-huge">Redefining What Business Class Means</h1>
          <p className="body-large about-hero-subtitle">
            Business class isn't about flights. It's about how your business operates, 
            serves customers, and scales with excellence.
          </p>
        </div>
      </section>

      {/* Our Belief */}
      <section className="belief-section">
        <div className="section-content">
          <div className="belief-grid">
            <div className="belief-text">
              <h2 className="display-medium">{aboutSections.belief.title}</h2>
              <p className="body-large">{aboutSections.belief.content}</p>
            </div>
            <div className="belief-comparison">
              <div className="comparison-card economy-card">
                <div className="comparison-title">Economy Mode</div>
                <ul className="comparison-list">
                  <li>Unstructured operations</li>
                  <li>Reactive decision making</li>
                  <li>Inconsistent results</li>
                  <li>Founder dependency</li>
                  <li>Unpredictable growth</li>
                </ul>
              </div>
              <div className="comparison-card business-class-card">
                <div className="comparison-title">Business Class</div>
                <ul className="comparison-list">
                  <li>Structured systems</li>
                  <li>Strategic planning</li>
                  <li>Predictable outcomes</li>
                  <li>Team independence</li>
                  <li>Scalable growth</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="mission-section">
        <div className="section-content">
          <div className="mission-content">
            <h2 className="display-medium">{aboutSections.mission.title}</h2>
            <p className="body-large mission-text">{aboutSections.mission.content}</p>
            <div className="mission-quote">
              "We work with ambitious founders who refuse to settle for mediocrity."
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="what-we-do-section">
        <div className="section-content">
          <h2 className="display-medium text-center">{aboutSections.whatWeDo.title}</h2>
          <div className="what-we-do-grid">
            {aboutSections.whatWeDo.points.map((point, idx) => {
              const icons = [Zap, Target, TrendingUp];
              const Icon = icons[idx];
              return (
                <div key={idx} className="what-we-do-card">
                  <Icon size={32} className="what-we-do-icon" />
                  <p className="body-large">{point}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="why-we-exist-section">
        <div className="section-content">
          <h2 className="display-medium">{aboutSections.whyWeExist.title}</h2>
          <div className="why-we-exist-content">
            <div className="problems-section">
              <h3 className="heading-2">Common Problems We Solve</h3>
              <div className="problems-grid">
                {aboutSections.whyWeExist.problems.map((problem, idx) => (
                  <div key={idx} className="problem-item">
                    <div className="problem-bullet"></div>
                    <p className="body-medium">{problem}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="solution-section">
              <Shield size={48} className="solution-icon" />
              <p className="body-large solution-text">{aboutSections.whyWeExist.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="approach-section">
        <div className="section-content">
          <h2 className="display-medium text-center">Our Approach</h2>
          <p className="body-medium text-center section-subtitle">
            Simple 4-step framework for transformation
          </p>
          <div className="approach-grid">
            {howItWorks.map(step => (
              <div key={step.step} className="approach-step-card">
                <div className="approach-step-number">
                  <span className="step-num">{step.step}</span>
                </div>
                <h3 className="heading-2">{step.title}</h3>
                <p className="body-medium">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Vision */}
      <section className="community-vision-section">
        <div className="section-content">
          <div className="community-vision-content">
            <h2 className="display-medium">The Business Class Community</h2>
            <p className="body-large">
              Beyond consulting, we've built an exclusive network for founders who are serious 
              about scale. This isn't a casual networking group—it's a growth-focused environment 
              where ambitious entrepreneurs help each other reach the next level.
            </p>
            <div className="community-vision-features">
              <div className="vision-feature">
                <div className="heading-3">Serious Founders Only</div>
                <p className="body-medium">Vetted members building real businesses with real revenue</p>
              </div>
              <div className="vision-feature">
                <div className="heading-3">Growth-Focused Environment</div>
                <p className="body-medium">Every interaction is designed to drive progress and action</p>
              </div>
              <div className="vision-feature">
                <div className="heading-3">No Fluff, Only Value</div>
                <p className="body-medium">Frameworks, systems, and strategies that actually work</p>
              </div>
            </div>
            <Link to="/community">
              <Button className="btn-primary mt-8">
                <span>Explore Community Tiers</span>
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="closing-statement-section">
        <div className="section-content">
          <div className="closing-statement-content">
            <h2 className="display-large">"Business class isn't a luxury. It's a standard."</h2>
            <p className="body-large">
              The question is: are you ready to upgrade?
            </p>
            <div className="closing-cta-buttons">
              <Link to="/consulting">
                <Button className="btn-primary">
                  <span>Work With Us</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/community">
                <Button className="btn-secondary">
                  <span>Join the Community</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
